/**
 * Life4Earth V2 — React hook for loading finance series data from the API.
 *
 * Mirrors: api/useApiScheduleData.ts but for finance cost data.
 * Returns finance points in the seed-data shape the existing pipeline expects.
 *
 * Includes fallback to empty array if the API is unreachable
 * (finance has no static seed data file, unlike schedule).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllBudgetSeriesPoints,
  financePointToSeedShape,
} from "./budgetSeriesPoints";
import type { BudgetSeriesPointResponse } from "./budgetSeriesPoints";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

export interface UseApiFinanceDataResult {
  /** All finance points in seed-data shape (project_GUID, reporting_date, etc.) */
  budgetData: Record<string, any>[];
  /** Raw API responses (for components that want the new shape) */
  raw: BudgetSeriesPointResponse[];
  /** True while the initial fetch is in progress */
  loading: boolean;
  /** Error message if the API call failed */
  error: string | null;
  /** Whether we're using API data or fell back */
  source: "api" | "none" | "loading";
  /** Force a refetch from the API */
  refetch: () => void;
}

export function useApiBudgetData(): UseApiFinanceDataResult {
  const [budgetData, setFinanceData] = useState<Record<string, any>[]>([]);
  const [raw, setRaw] = useState<BudgetSeriesPointResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "none" | "loading">("loading");
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    invalidateAndRefetch(QUERY_KEYS.finance);
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchForKey(
      QUERY_KEYS.finance,
      async () => {
        const apiPoints = await fetchAllBudgetSeriesPoints();

        // Deduplicate by (project_GUID, reporting_date): if the DB has
        // stale duplicate rows (e.g. one with instance_guid=null and
        // another with a real instance_guid for the same date), keep
        // the most-recently-created row so the budget curve is clean.
        const seeds = apiPoints.map(financePointToSeedShape);
        const deduped = new Map<string, Record<string, any>>();
        for (const s of seeds) {
          const key = `${s.project_GUID}|${s.reporting_date}`;
          const prev = deduped.get(key);
          // Keep the entry with an instance_guid over null, or the later one
          if (!prev || (s.instance_guid && !prev.instance_guid)) {
            deduped.set(key, s);
          }
        }
        return { raw: apiPoints, transformed: Array.from(deduped.values()) };
      }
    )
      .then((result) => {
        if (!mountedRef.current) return;
        setRaw(result.raw);
        setFinanceData(result.transformed);
        setSource("api");
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.warn(
          "[useApiBudgetData] API unreachable, no finance data available:",
          err.message
        );
        setError(err.message);
        setFinanceData([]);
        setRaw([]);
        setSource("none");
        setLoading(false);
      });
  }, []);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === QUERY_KEYS.finance) {
        load();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [load]);

  return { budgetData, raw, loading, error, source, refetch };
}
